import React, { useState, useEffect, useRef } from "react";
import {
  Headphones, Smartphone, Phone, PhoneOff, PhoneIncoming,
  Mic, MicOff, Pause, Play, Grid3x3, X, AlertTriangle, RefreshCw, Loader2,
} from "lucide-react";
import { useSoftphoneContext } from "../../contexts/SoftphoneContext";
import "./softphone.scss";

/**
 * The browser softphone.
 *
 * Mounted once globally. Renders only for counsellors who have actually been
 * given a SIP identity — for everyone else browser calling does not exist, and a
 * widget explaining a feature they cannot use is just clutter in the corner of
 * every page.
 *
 * ── What this widget is really for ──
 * A two-leg call is invisible. In phone mode the counsellor's own handset rings
 * and that is its own signal; in browser mode there is no signal at all unless
 * something on screen provides one. So the widget's job, in order of importance:
 *
 *  1. Say whether the softphone is actually connected. A silently-dropped
 *     registration means calls ring nowhere, and nothing else would reveal it.
 *  2. Present the live call with the controls a phone would have.
 *  3. Say, in words, when it has fallen back to the phone and why — a softphone
 *     that fails closed loses the call; one that fails over just rings a mobile.
 *
 * Inbound screen-pop is deliberately NOT re-implemented here: `IncomingCallPopup`
 * already handles it from the socket event, identically in both modes, so the
 * caller context a counsellor sees before answering never depends on which device
 * is ringing.
 */

const STATUS_META = {
  registering: { label: "Connecting…", tone: "pending" },
  registered: { label: "Ready", tone: "ok" },
  failed: { label: "Not connected", tone: "fail" },
  unavailable: { label: "Unavailable", tone: "muted" },
  idle: { label: "Phone mode", tone: "muted" },
};

const DTMF_KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];

/** Live call timer. Counts from the answer, not from the dial. */
function CallTimer({ answeredAt }) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!answeredAt) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [answeredAt]);

  if (!answeredAt) return null;
  const total = Math.max(0, Math.floor((now - answeredAt) / 1000));
  const m = String(Math.floor(total / 60)).padStart(2, "0");
  const s = String(total % 60).padStart(2, "0");
  return <span className="sp-timer">{m}:{s}</span>;
}

export default function Softphone() {
  const {
    available, status, reason, activeCall, mode, canChooseMode,
    answer, hangup, toggleMute, toggleHold, sendDtmf, setPreferredMode, retry,
  } = useSoftphoneContext();

  const [collapsed, setCollapsed] = useState(true);
  const [showDialpad, setShowDialpad] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [dismissedReason, setDismissedReason] = useState("");
  const prevCallRef = useRef(null);

  // A ringing call opens the widget by itself. Requiring a click to see the call
  // you are being asked to answer would defeat the point of having it.
  useEffect(() => {
    if (activeCall && !prevCallRef.current) {
      setCollapsed(false);
      setShowDialpad(false);
    }
    prevCallRef.current = activeCall;
  }, [activeCall]);

  if (!available) return null;

  const meta = STATUS_META[status] || STATUS_META.idle;
  const inBrowserMode = mode === "webrtc";
  // Worth interrupting for: browser mode is on but the registration isn't live,
  // so calls are quietly going to the counsellor's phone instead.
  const degraded = inBrowserMode && status !== "registered" && status !== "registering";

  const switchMode = async (next) => {
    setSwitching(true);
    try {
      await setPreferredMode(next);
    } catch {
      /* the hook re-reads config; a failed switch simply leaves the old mode */
    }
    setSwitching(false);
  };

  const phase = activeCall?.phase;

  return (
    <div className={`sp ${collapsed && !activeCall ? "sp--collapsed" : ""}`}>
      {/* ── Header: the status line, always visible ── */}
      <button
        type="button"
        className="sp__head"
        onClick={() => setCollapsed((c) => !c)}
        aria-expanded={!collapsed}
      >
        {inBrowserMode ? <Headphones size={15} /> : <Smartphone size={15} />}
        <span className="sp__head-label">
          {inBrowserMode ? "Browser calling" : "Phone calling"}
        </span>
        <span className={`sp__dot sp__dot--${degraded ? "fail" : meta.tone}`} />
        <span className="sp__head-status">
          {status === "registering" && <Loader2 size={11} className="sp__spin" />}
          {inBrowserMode ? meta.label : "Your phone rings"}
        </span>
      </button>

      {/* ── The degraded banner: the reason the fallback is credible ── */}
      {degraded && dismissedReason !== reason && (
        <div className="sp__alert" role="status">
          <AlertTriangle size={13} />
          <div>
            <strong>Calls are ringing your phone.</strong>
            <span>{reason || "The softphone isn't connected."}</span>
          </div>
          <div className="sp__alert-actions">
            {status === "failed" && (
              <button type="button" onClick={retry} title="Try again">
                <RefreshCw size={12} />
              </button>
            )}
            <button type="button" onClick={() => setDismissedReason(reason)} aria-label="Dismiss">
              <X size={12} />
            </button>
          </div>
        </div>
      )}

      {!collapsed && (
        <div className="sp__body">
          {/* ── Live call ── */}
          {activeCall ? (
            <div className="sp__call">
              <div className="sp__call-who">
                {phase === "incoming"
                  ? <PhoneIncoming size={16} className="sp__call-icon sp__call-icon--ring" />
                  : <Phone size={16} className="sp__call-icon" />}
                <div>
                  <span className="sp__call-number">
                    {activeCall.from || activeCall.to || "Unknown number"}
                  </span>
                  <span className="sp__call-phase">
                    {phase === "incoming" ? "Incoming call"
                      : phase === "ringing" ? "Ringing…"
                      : activeCall.held ? "On hold"
                      : "Connected"}
                    {activeCall.answeredAt && <CallTimer answeredAt={activeCall.answeredAt} />}
                  </span>
                </div>
              </div>

              <div className="sp__controls">
                {phase === "incoming" && (
                  <button type="button" className="sp__btn sp__btn--answer" onClick={answer}>
                    <Phone size={15} /> Answer
                  </button>
                )}

                {phase === "active" && (
                  <>
                    <button
                      type="button"
                      className={`sp__btn ${activeCall.muted ? "is-on" : ""}`}
                      onClick={toggleMute}
                      title={activeCall.muted ? "Unmute" : "Mute"}
                    >
                      {activeCall.muted ? <MicOff size={15} /> : <Mic size={15} />}
                    </button>
                    <button
                      type="button"
                      className={`sp__btn ${activeCall.held ? "is-on" : ""}`}
                      onClick={toggleHold}
                      title={activeCall.held ? "Resume" : "Hold"}
                    >
                      {activeCall.held ? <Play size={15} /> : <Pause size={15} />}
                    </button>
                    <button
                      type="button"
                      className={`sp__btn ${showDialpad ? "is-on" : ""}`}
                      onClick={() => setShowDialpad((d) => !d)}
                      title="Keypad"
                    >
                      <Grid3x3 size={15} />
                    </button>
                  </>
                )}

                <button
                  type="button"
                  className="sp__btn sp__btn--hangup"
                  onClick={hangup}
                  title="Hang up"
                >
                  <PhoneOff size={15} />
                </button>
              </div>

              {/* A keypad is not decoration: IVR menus and extension numbers are
                  unreachable without one, and that is most institutional numbers. */}
              {showDialpad && phase === "active" && (
                <div className="sp__dialpad">
                  {DTMF_KEYS.map((key) => (
                    <button type="button" key={key} onClick={() => sendDtmf(key)}>{key}</button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <p className="sp__idle">
              {inBrowserMode
                ? status === "registered"
                  ? "Ready. Calls you place from an enquiry will open here."
                  : "Not connected — calls will ring your phone."
                : "Calls you place will ring your own phone first, then dial the student."}
            </p>
          )}

          {/* ── Mode switch ── */}
          {canChooseMode && !activeCall && (
            <div className="sp__modes">
              <button
                type="button"
                className={`sp__mode ${!inBrowserMode ? "is-on" : ""}`}
                disabled={switching}
                onClick={() => switchMode("phone")}
              >
                <Smartphone size={13} /> My phone
              </button>
              <button
                type="button"
                className={`sp__mode ${inBrowserMode ? "is-on" : ""}`}
                disabled={switching}
                onClick={() => switchMode("webrtc")}
              >
                <Headphones size={13} /> Browser
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
